import React, {
  Fragment,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import {
  useHighlightingContext,
  withHighlightingContext,
} from '../../../HighlightingContext';
import {
  addNewEventAction,
  IRow,
  IRowMeta,
  RowType,
} from '../../../../redux/slices/oldBuilder';
import { IStyled } from '../../../../styles/types';
import {
  activeSchemaIdSelector,
  eventConditionsPrimitiveSelector,
  functionPrimitiveSelector,
} from '../../../../redux/selectors/builder';
import * as BuilderUI from '../../BuilderUI';
import EventCondition from './EventCondition';
import SaveVirtualEventModal from './SaveVirtualEventModal';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { MAX_EVENT_CONDITIONS_COUNT } from '../../../../config/constants';
import { RowActionsMenu } from '../../BuilderActions';
import { getEventConditionErrors } from '../../../../utils/builder/validation';
import {
  EventKind,
  IEventConditionFieldsFragment,
  PermissionKey,
} from '../../../../utils/apollo/graphqlSchema';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../../../pages/PermissionControls/PermissionElementWrapper';
import { isEmptyEventCondition } from '../../../../utils/builder';
import { generateEventsInputByEvents } from '../../utils/builderUtils';
import { useActionsVisibilityContext } from '../../../ActionsVisibilityContext';
import { byTypeName } from '../../../../utils/types';
import { SingleSelectOption } from '../../../Dropdown/SingleSelect';

interface IProps extends IStyled {
  rowMeta: IRowMeta;
  label?: ReactNode;
  showLabelPerCondition?: boolean;
  onRemoveCondition?: () => void;
}

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  column-gap: 4px;
  width: 100%;
`;

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

const DividerWrapper = styled.div`
  padding-left: 4px;
`;

const Label = styled(BuilderUI.InputLabel)`
  margin-top: 10px;
  padding-left: 4px;
  line-height: normal;
`;

const allowAddEventRowTypes = [
  RowType.paths,
  RowType.steps,
  RowType.retention,
  RowType.mergeEvents,
];

const getAddEventTooltipContent = (rowMeta: IRowMeta) => {
  const { rowType, rowIndex } = rowMeta;

  if (rowType === RowType.paths) {
    if (rowIndex === 0) {
      return 'Add additional events to the Starting Event(s) step';
    } else {
      return 'Add additional events to the Ending Event(s) step';
    }
  }

  if (rowType === RowType.retention || rowType === RowType.steps) {
    return 'Add Event(s) to an existing step with OR boolean logic.';
  }

  return 'Add Event(s) to an existing measure with OR boolean logic.';
};

const showSaveVirtualEventAction = (
  fn: IRow['fn'],
  eventConditions: IRow['eventConditions'],
) => {
  if (fn?.fieldRequired) {
    return false;
  }

  const eventConditionErrors = getEventConditionErrors(fn, eventConditions);
  // allow save virtual event only if all anyEvent has filter
  const isAllAnyEventHasFilter = eventConditions.every((eventCondition) => {
    const events = eventCondition.events;
    const findAnyEvent = events.find((event) => event.kind === EventKind.Any);
    if (findAnyEvent && eventCondition.filter) {
      return true;
    } else if (findAnyEvent) {
      return false;
    }

    return true;
  });
  return isAllAnyEventHasFilter && isEmpty(eventConditionErrors);
};

const EventConditions = ({
  rowMeta,
  label,
  className,
  showLabelPerCondition,
  onRemoveCondition,
}: IProps) => {
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const dispatch = useAppDispatch();

  const { rowType, rowIndex, cohortRowType } = rowMeta;

  const eventConditionsSelector = useMemo(eventConditionsPrimitiveSelector, []);
  const eventConditions: IEventConditionFieldsFragment[] = useAppSelector(
    (state) => eventConditionsSelector(state, rowMeta),
  );

  const functionSelector = useMemo(functionPrimitiveSelector, []);
  const rowFn = useAppSelector((state) => functionSelector(state, rowMeta));

  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const { checkIsPermitted } = usePermission();

  const isPermittedToDisplaySaveVirtualEventButton = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.VirtualEventCreate,
      PermissionKey.VirtualEventEdit,
    ],
    validateKind: 'OR',
    schemaId: activeSchemaId,
  });

  const {
    activateHighlighting,
    deactivateHighlighting,
    clearHighlightedByTimeout,
  } = useHighlightingContext();
  const { hideActions } = useActionsVisibilityContext();

  const handleAddEvent = useCallback(() => {
    dispatch(addNewEventAction(rowMeta));
  }, [dispatch, rowMeta]);

  const handleSaveClick = useCallback(() => {
    setSaveModalOpen(true);

    deactivateHighlighting();
    hideActions();
  }, [deactivateHighlighting, hideActions]);

  const handleSubmitVirtualEvent = () => {
    setSaveModalOpen(false);

    activateHighlighting();

    clearHighlightedByTimeout();
  };

  const allowAddEvents =
    eventConditions.length < MAX_EVENT_CONDITIONS_COUNT &&
    (allowAddEventRowTypes.includes(rowType) ||
      (rowFn && !rowFn.fieldRequired));

  const allowRemoveCondition =
    rowType === RowType.mergeEvents || eventConditions.length > 1;

  const allowSaveVirtualEvent = showSaveVirtualEventAction(
    rowFn,
    eventConditions,
  );

  const canRenderProperty =
    rowFn?.fieldRequired && eventConditions.length === 1;

  const eventConditionKeyPrefix = `event-condition-${rowType}-${rowIndex}${
    cohortRowType || ''
  }`;

  const getEventConditionActions = useCallback(
    (eventCondition: IEventConditionFieldsFragment, eventIndex: number) => {
      const result: ReactNode[] = [];

      if (allowAddEvents) {
        result.push(
          <SingleSelectOption
            key={`${eventConditionKeyPrefix}-add-event`}
            onClick={handleAddEvent}
            label={'Add Event'}
            tooltipOption={{
              content: getAddEventTooltipContent(rowMeta),
            }}
          />,
        );
      }

      if (allowSaveVirtualEvent && eventIndex === 0) {
        const isEventVirtual =
          eventCondition.events[eventIndex].kind === EventKind.Virtual;
        result.push(
          <PermissionElementWrapper
            key={`${eventConditionKeyPrefix}-save-virtual-event`}
            isPermitted={isPermittedToDisplaySaveVirtualEventButton}
          >
            <SingleSelectOption
              onClick={handleSaveClick}
              label={'Save Virtual Event'}
              onMouseEnter={activateHighlighting}
              onMouseLeave={deactivateHighlighting}
              tooltipOption={{
                content:
                  'Save a Virtual Event which concatenates individual events together.',
              }}
              disabled={
                !isPermittedToDisplaySaveVirtualEventButton || isEventVirtual
              }
            />
          </PermissionElementWrapper>,
        );
      }

      if (result?.length) {
        return <RowActionsMenu>{result}</RowActionsMenu>;
      } else {
        return null;
      }
    },
    [
      activateHighlighting,
      allowAddEvents,
      allowSaveVirtualEvent,
      deactivateHighlighting,
      eventConditionKeyPrefix,
      handleAddEvent,
      handleSaveClick,
      isPermittedToDisplaySaveVirtualEventButton,
      rowMeta,
    ],
  );

  const getPlaceholder = () => {
    let placeholder = 'Select event(s) to measure';

    if (rowType === RowType.paths) {
      if (rowIndex === 0) {
        return 'Select starting event(s) to measure';
      }
      if (rowIndex === 1) {
        return 'Select ending event(s) to measure';
      }
    }

    if (rowType === RowType.retention) {
      if (rowIndex === 0) {
        return 'Select starting event(s) to measure';
      }
      if (rowIndex === 1) {
        return 'Select returning event(s) to measure';
      }
    }

    return placeholder;
  };

  return (
    <Container className={className}>
      {label && !showLabelPerCondition && <Label>{label}</Label>}
      <Wrapper>
        {eventConditions.map((eventCondition, eventIndex) => {
          const { events } = eventCondition;

          const placeholder = getPlaceholder();

          const actions = getEventConditionActions(eventCondition, eventIndex);
          const virtualEvents = events.filter(byTypeName('VirtualEvent'));
          const canRenderFilters =
            !isEmptyEventCondition(eventCondition) && !virtualEvents?.length;

          return (
            <Fragment key={`${eventConditionKeyPrefix}-${eventIndex}-item`}>
              {eventIndex > 0 && (
                <DividerWrapper>
                  <BuilderUI.HorizontalDivider />
                </DividerWrapper>
              )}
              <EventCondition
                rowMeta={{ ...rowMeta, eventIndex }}
                placeholder={placeholder}
                actions={actions}
                label={label && showLabelPerCondition ? label : null}
                eventsInput={generateEventsInputByEvents(events)}
                onRemove={onRemoveCondition}
                canRenderRemove={allowRemoveCondition}
                filter={eventCondition?.filter}
                canRenderFilters={canRenderFilters}
                canRenderProperty={canRenderProperty}
              />
            </Fragment>
          );
        })}
      </Wrapper>
      {saveModalOpen && activeSchemaId && (
        <SaveVirtualEventModal
          open
          onSubmit={handleSubmitVirtualEvent}
          onClose={() => setSaveModalOpen(false)}
          activeSchemaId={activeSchemaId}
          eventConditions={eventConditions || []}
          rowMeta={rowMeta}
        />
      )}
    </Container>
  );
};

export default withHighlightingContext(EventConditions);
